import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { speakingEngagements } from "@/mocks/home";
import { usePageSEO } from '@/hooks/usePageSEO';

const speakingSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Jeff Dumè',
    url: 'https://jeffdume.com/speaking',
    image: 'https://jeffdume.com/images/Jeff_D-28.jpg',
    jobTitle: 'Speaker & Workforce Intelligence Advocate',
    description: 'Jeff Dumé speaks on workforce intelligence, responsible AI, and organizational systems that support stronger teams and better decisions.',
    knowsAbout: ['Workforce Intelligence', 'Responsible AI', 'Structured Hiring', 'Leadership', 'Entrepreneurship', 'Operational Systems'],
    sameAs: ['https://www.linkedin.com/in/jdume/'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'Gulf Coast Symposium on HR Issues',
    description: 'Jeff Dumè on workforce intelligence and responsible AI',
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: 'Gulf Coast Symposium',
      address: { '@type': 'PostalAddress', addressRegion: 'TX', addressCountry: 'US' },
    },
    organizer: { '@type': 'Organization', name: 'Gulf Coast Symposium on HR Issues' },
    performer: { '@type': 'Person', name: 'Jeff Dumè' },
  },
];

export default function Speaking() {
  usePageSEO({
    title: 'Jeff Dumé | Speaker on Workforce Intelligence & Responsible AI | Houston, TX',
    description: 'Jeff Dumé speaks on workforce intelligence, responsible AI, and organizational systems at HR Houston, Gulf Coast Symposium, and industry events. Book Jeff for your next event.',
    keywords: 'Jeff Dumé speaker, workforce intelligence speaker, responsible AI speaker, HR Houston speaker, Gulf Coast Symposium, hiring infrastructure, leadership speaker Texas, BuildFit AI',
    canonical: '/speaking',
    ogTitle: 'Jeff Dumé | Building Conversations That Shape Better Decisions',
    ogDescription: 'Speaker at HR Houston and Gulf Coast Symposium. Topics include responsible AI, hiring as infrastructure, workforce strategy, and operational leadership.',
    schema: speakingSchema,
    breadcrumbs: [{ name: 'Speaking', item: '/speaking' }],
  });

  const sortedEngagements = [...speakingEngagements];

  return (
    <div className="bg-charcoal min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-20 sm:pt-24 md:pt-28 pb-12 md:pb-20">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-10 lg:gap-16 items-start">
              <div className="lg:w-[60%] w-full">
                <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
                  SPEAKING
                </p>
                <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-display font-bold leading-tight mb-4 sm:mb-6 font-display">
                  Building Conversations That Shape Better Decisions
                </h1>
                <p className="text-white/60 text-sm md:text-base leading-relaxed max-w-xl">
                  Jeff Dumé speaks on workforce intelligence, responsible AI, and organizational systems that support stronger teams and better decisions.
                </p>
                <p className="text-white/40 text-sm leading-relaxed max-w-xl mt-4">
                  His presentations combine practical experience, operational thinking, and lessons from building organizations and technologies designed for sustainable growth.
                </p>
              </div>

              <div className="lg:w-[40%] w-full max-w-xs sm:max-w-sm lg:max-w-none mx-auto lg:mx-0">
                <div className="relative">
                  <div className="w-full aspect-[4/3] rounded-xl overflow-hidden border border-white/10 bg-dark-card">
                    <img
                      src="/images/speaking-event.jpg"
                      alt="Jeff Dumè speaking on workforce intelligence and responsible AI"
                      loading="eager"
                      fetchPriority="high"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  <div className="absolute -bottom-3 -left-3 w-full h-full rounded-xl border border-gold/20 -z-10"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Speaking Engagements */}
        <section className="pb-12 md:pb-20">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <p className="text-white/30 text-[10px] font-semibold tracking-[0.2em] uppercase mb-6">
              Recent Engagements
            </p>
            <div className="space-y-5 sm:space-y-8">
              {sortedEngagements.map((event) => (
                <div
                  key={event.id}
                  className="bg-dark-card rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 hover:border-gold/20 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-gold font-bold text-sm">{event.year}</span>
                  </div>
                  <h2 className="text-white font-semibold text-lg md:text-xl mb-2">
                    {event.event}
                  </h2>
                  <p className="text-gold text-sm font-medium mb-3">{event.topic}</p>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {event.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Speaking Topics */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 text-center">
              Areas of Focus
            </p>
            <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight mb-10 text-center">
              What Jeff Covers
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {[
                {
                  title: 'Responsible AI Use',
                  desc: 'Where AI belongs in organizational decision making and where it does not. A grounded look at adoption, oversight, and keeping people in control of critical outcomes.',
                  icon: 'ri-robot-2-line',
                },
                {
                  title: 'Hiring as Infrastructure',
                  desc: 'Treating hiring with the same rigor as financial controls or operational safety. Why structured evaluation protects organizations from costly mis hires and builds stronger teams over time.',
                  icon: 'ri-building-3-line',
                },
                {
                  title: 'Workforce Intelligence',
                  desc: 'Moving beyond filling seats to building capability. How organizations can improve hiring outcomes, reduce turnover, and create stronger long term workforce performance.',
                  icon: 'ri-brain-line',
                },
                {
                  title: 'Operational Leadership',
                  desc: 'What it takes to run organizations that execute, not just plan. Systems, accountability, and the discipline to make hard calls before they become crises.',
                  icon: 'ri-settings-6-line',
                },
                {
                  title: 'Building & Scaling Businesses',
                  desc: 'Lessons from the ground. Navigating growth phases, building products, making early hires that shape culture, and knowing when to say no.',
                  icon: 'ri-rocket-line',
                },
                {
                  title: 'Safety, Compliance & Oversight',
                  desc: 'For regulated and high stakes industries, the balance between innovation and liability. Practical approaches to adopting new tools without exposing the organization to unnecessary risk.',
                  icon: 'ri-shield-check-line',
                },
              ].map((topic) => (
                <div
                  key={topic.title}
                  className="bg-dark-card rounded-xl p-6 md:p-8 border border-white/5 hover:border-gold/20 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                      <i className={`${topic.icon} text-gold text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-base mb-1">
                        {topic.title}
                      </h3>
                      <p className="text-white/40 text-sm leading-relaxed">{topic.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Audience */}
            <div className="mt-10 sm:mt-12 text-center">
              <p className="text-white/30 text-xs font-semibold tracking-[0.2em] uppercase mb-5">
                Who This Is For
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                {[
                  'HR Leaders',
                  'Founders',
                  'Construction Leaders',
                  'Operations Leaders',
                ].map((audience) => (
                  <span
                    key={audience}
                    className="inline-flex items-center gap-1.5 bg-dark-card rounded-full px-4 py-2 border border-white/5"
                  >
                    <i className="ri-user-follow-line text-gold text-sm"></i>
                    <span className="text-white/70 text-sm font-medium">{audience}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* BuildFit AI Section */}
        <section className="py-16 md:py-24 lg:py-28 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <div className="bg-dark-card rounded-2xl p-8 md:p-12 lg:p-14 border border-white/5">
              <div className="text-center mb-8">
                <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-4">
                  BuildFit AI&trade;
                </p>
                <h2 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-4 font-display">
                  The Company Behind the Mission
                </h2>
                <p className="text-white/50 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
                  BuildFit AI helps organizations reduce mis-hires, improve retention, and create more consistent hiring decisions. Structured evaluation, not automated hiring.
                </p>
              </div>

              <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-8">
                <p>
                  Organizations replace gut-feel interviews and inconsistent assessments with frameworks built around what success actually looks like in the role.
                </p>
                <p className="text-white/80 font-medium">
                  We do not automate hiring. We standardize how it is evaluated.
                </p>
                <p>
                  BuildFit AI assists the process. It does not make the decision.
                </p>
                <p className="text-gold font-semibold">
                  Every hire remains fully human-led.
                </p>
              </div>

              <div className="text-center">
                <a
                  href="https://buildfitai.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gold text-charcoal font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap"
                >
                  Learn More About BuildFit AI
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 border-t border-white/5">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gold/30 bg-dark-card">
                <img
                  src="/images/Jeff_D-28.jpg"
                  alt="Jeff Dumè"
                  loading="eager"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <h2 className="text-white text-xl md:text-2xl font-bold mb-4">
              Book Jeff for Your Next Event
            </h2>
            <p className="text-white/40 text-sm md:text-base mb-8 max-w-lg mx-auto leading-relaxed">
              Available for keynotes, panels, roundtables, and executive briefings. Reach out to discuss topics, dates, and format.
            </p>
            <a
              href="mailto:jeff.dume@mainframesolutions.net"
              className="inline-flex items-center gap-2 bg-gold text-charcoal font-semibold text-sm px-8 py-4 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap"
            >
              Request Speaking Inquiry
              <i className="ri-send-plane-line"></i>
            </a>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-8 h-[1px] bg-white/10"></span>
              <div className="w-2 h-2 rounded-full bg-gold/40"></div>
              <span className="w-8 h-[1px] bg-white/10"></span>
            </div>
            <p className="text-white/20 text-xs tracking-wide uppercase">
              Conversations that move organizations forward
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}